<!-- Active Filters bar: slides in/out with same grid-template-rows pattern as the drawer -->
<div class="active-filters-wrapper" [class.active-filters-visible]="activeFilterChips.length > 0">
  <div class="active-filters-inner">
    <div class="active-filters-bar">
      <!-- Label is projected into rms-filter-chip-list so it shares a single flex-wrap context
           with the chips — when chips overflow to a second row they wrap flush-left under the
           label rather than indenting under the first chip. -->
      <rms-filter-chip-list [items]="activeFilterChips" (onDeleteOption)="removeFilter($event)">
        <span class="filter-label">Active Filters:</span>
        <!-- Projected after the chips so on phone it flows inline as the row's last item; on
             desktop it is absolutely pinned to the bar's right edge instead. -->
        <button chipListEnd class="bar-clear-btn" (click)="handleFilterReset()"><span class="clear-x">&times;</span> Clear Filters</button>
      </rms-filter-chip-list>
    </div>
  </div>
</div>

<!-- Phone only (CSS-gated): the pane opens as a drawer from this button. It never moves, so a
     thumb resting on it can still scroll the page; it collapses to a circle on first scroll. -->
<button type="button" class="filter-fab" [class.collapsed]="fabCollapsed" (click)="openPane()"
        [attr.aria-label]="activeFilterChips.length > 0 ? 'Filters (' + activeFilterChips.length + ')' : 'Filters'">
  <svg class="filter-fab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
  <span class="filter-fab-label" aria-hidden="true">Filters</span>
  <span class="filter-fab-count" *ngIf="activeFilterChips.length > 0" aria-hidden="true">({{activeFilterChips.length}})</span>
</button>

<!-- Phone only: dims the page behind the open drawer and closes it on tap. Kept out of the tab
     order — the drawer's ✕ is the keyboard close path. -->
<button type="button" class="filter-drawer-backdrop" [class.open]="isOpen"
        tabindex="-1" aria-hidden="true" (click)="closePane()"></button>

<div class="filter-pane-container" [class.filter-pane-open]="isOpen"
     [attr.role]="isMobile ? 'dialog' : null" [attr.aria-modal]="isMobile ? true : null"
     [attr.aria-label]="isMobile ? 'Filters' : null" [attr.aria-hidden]="isMobile && !isOpen ? true : null">
  <div class="filter-pane-inner">
    <div class="card-header">
      <div class="filter-options-label">Filter Options</div>
      <button type="button" class="filter-close-btn" (click)="closePane()">&times;</button>
    </div>
    <!-- Phone only: the drawer's first row — the page's own view tools, mirrored, plus the
         drawer's only close control. Pinned outside the scrolling body so the ✕ can't scroll away. -->
    <div class="drawer-page-tools">
      <ng-content select="[drawerTools]"></ng-content>
      <button type="button" class="drawer-close" (click)="closePane()" aria-label="Close filters">&times;</button>
    </div>
    <form id="filterPaneForm" (keydown)="disableEnterKeyEvent($event)">
      <!-- A page's own groups (customGroups), each marked filterGroup so it lands as a direct child of the form -->
      <ng-content select="[filterGroup]"></ng-content>
      <ng-container *ngIf="!customGroups">
        <div class="filter-group d-flex flex-column">
          <div class="filter-group-label">Domains:</div>
          <rms-domain-filter [variant]="isMobile ? 'grid' : 'default'" [resetFilterSubject]="resetFilterSubject"
            [initialDomainIds]="formData.domainIds"
            (domainFilterToggleEvent)="handleDomainFilterToggle($event)"></rms-domain-filter>
        </div>

        <div class="filter-divider" *ngIf="hasPortfolioPermission"></div>

        <div class="filter-group" *ngIf="hasPortfolioPermission">
          <div class="filter-group-label">Portfolio Type:</div>
          <div class="portfolio-type-grid">
            <ng-container *ngFor="let portfolio of portfolios">
              <label for="{{portfolio.name}}" class="custom-checkbox">
                <input #portfolioInput type="checkbox" name="{{portfolio.name}}" id="{{portfolio.name}}" [attr.data-portfolioId]="portfolio.id"
                  [checked]="formData.portfolioIds.includes(portfolio.id)"
                  (input)="onInputChange($event, 'portfolio.id')" class="portfolio-type">
                <span class="checkmark"></span>
                {{portfolio.name}}
              </label>
            </ng-container>
          </div>
        </div>

        <div class="filter-divider" *ngIf="hasPortfolioPermission"></div>

        <div class="filter-group portfolio-options-group" *ngIf="hasPortfolioPermission">
          <div class="filter-group-label">Portfolio Options:</div>
          <label for="Long" class="custom-checkbox">
            <input type="checkbox" id="Long" name="Long" [checked]="formData.positionType.includes('LONG')"
              (input)="onInputChange($event, 'long')" class="position-type">
            <span class="checkmark"></span>
            Long
          </label>
          <label for="Short" class="custom-checkbox">
            <input type="checkbox" id="Short" name="Short" [checked]="formData.positionType.includes('SHORT')"
              (input)="onInputChange($event, 'short')" class="position-type">
            <span class="checkmark"></span>
            Short
          </label>
        </div>

        <div class="filter-divider"></div>

        <div class="filter-group">
          <div class="filter-group-label">Following:</div>
          <button type="button" class="filter-btn" (click)="toggleFilterFollowed()">
            <img [src]="!filterBtnActive ? assetsUrls.rss : assetsUrls.rssActive" alt="RSS">
          </button>
        </div>

        <div class="filter-divider"></div>

        <div class="filter-group market-cap">
          <div class="mk-cap-sel-container" [class.mk-cap-inactive]="isMarketCapRangeActive && !isMarketCapBucketActive">
            <div class="filter-group-label">Market Cap:</div>
            <label for="mkCapLarge" class="custom-checkbox">
              <input type="checkbox" name="mkCapLarge" id="mkCapLarge" [(ngModel)]="formData.mkCapLarge" (input)="onInputChange($event, 'mkCapLarge')">
              <span class="checkmark"></span>
              Large <span class="range-detail">(&thinsp;&gt; $10B&thinsp;)</span>
            </label>
            <label for="mkCapMedium" class="custom-checkbox">
              <input type="checkbox" name="mkCapMedium" id="mkCapMedium" [(ngModel)]="formData.mkCapMedium" (input)="onInputChange($event, 'mkCapMedium')">
              <span class="checkmark"></span>
              Medium <span class="range-detail">(&thinsp;$2–$10B&thinsp;)</span>
            </label>
            <label for="mkCapSmall" class="custom-checkbox">
              <input type="checkbox" name="mkCapSmall" id="mkCapSmall" [(ngModel)]="formData.mkCapSmall" (input)="onInputChange($event, 'mkCapSmall')">
              <span class="checkmark"></span>
              Small <span class="range-detail">(&thinsp;$250M–$2B&thinsp;)</span>
            </label>
            <label for="mkCapMicro" class="custom-checkbox">
              <input type="checkbox" name="mkCapMicro" id="mkCapMicro" [(ngModel)]="formData.mkCapMicro" (input)="onInputChange($event, 'mkCapMicro')">
              <span class="checkmark"></span>
              Micro <span class="range-detail">(&thinsp;&lt; $250M&thinsp;)</span>
            </label>
          </div>
          <span class="or-text">OR</span>
          <div class="filter-group mk-cap-range-group" [class.mk-cap-inactive]="isMarketCapBucketActive && !isMarketCapRangeActive">
            <div class="filter-group-label">Market Cap Range:</div>
            <div class="mk-cap-range-row">
              <span class="input-clearable" [class.has-value]="!!formData.mkCapLow">
                <input type="text" name="mkCapLow" placeholder="Min. MC" id="mkCapLow" [(ngModel)]="formData.mkCapLow"
                  (keypress)="onMarketCapKeypress($event)" (input)="onMarketCapInput('mkCapLow')"
                  (paste)="onMarketCapPaste($event)"
                  (blur)="onMarketCapRangeChange('mkCapLow')" (keydown.enter)="onMarketCapRangeChange('mkCapLow')">
                <button type="button" class="input-clear-x"
                        (click)="clearMkCapInput('mkCapLow')"
                        aria-label="Clear minimum market cap">&times;</button>
              </span>
              <span class="mk-cap-range-dash">&ndash;</span>
              <span class="input-clearable" [class.has-value]="!!formData.mkCapHigh">
                <input type="text" name="mkCapHigh" placeholder="Max. MC" id="mkCapHigh" [(ngModel)]="formData.mkCapHigh"
                  (keypress)="onMarketCapKeypress($event)" (input)="onMarketCapInput('mkCapHigh')"
                  (paste)="onMarketCapPaste($event)"
                  (blur)="onMarketCapRangeChange('mkCapHigh')" (keydown.enter)="onMarketCapRangeChange('mkCapHigh')">
                <button type="button" class="input-clear-x"
                        (click)="clearMkCapInput('mkCapHigh')"
                        aria-label="Clear maximum market cap">&times;</button>
              </span>
            </div>
          </div>
        </div>

      </ng-container>

      <div class="filter-pane-actions">
        <button type="button" class="filter-footer-clear" [class.visible]="activeFilterChips.length > 0" (click)="handleFilterReset()">Clear Filters</button>
        <button type="button" class="filter-footer-done" [class.has-filters]="activeFilterChips.length > 0" (click)="closePane()">Close</button>
      </div>

      <!-- Phone only: a live preview of what the filters remove, listing only the tables whose
           count changed. Shown whenever a filter is set, so a filter that removes nothing says so
           rather than leaving a gap. -->
      <div class="drawer-breakdown" [class.visible]="activeFilterChips.length > 0">
        <ng-container *ngIf="changedResultCounts.length > 0; else nothingFiltered">
          <div class="drawer-breakdown-line" *ngFor="let count of changedResultCounts; trackBy: trackByLabel">
            <b>({{count.filtered}} of {{count.total}})</b> {{count.label}}
          </div>
        </ng-container>
        <ng-template #nothingFiltered>
          <div class="drawer-breakdown-line">Nothing filtered out.</div>
        </ng-template>
      </div>
    </form>

    <!-- Phone only: non-scrolling footer, so both actions are reachable at any scroll position.
         Filters apply as they change, so the primary button only closes the drawer. -->
    <div class="drawer-footer">
      <button type="button" class="drawer-clear" *ngIf="activeFilterChips.length > 0"
              [class.is-primary]="!resultsLoading && totalResults === 0" (click)="handleFilterReset()">
        Clear Filters ({{activeFilterChips.length}})
      </button>
      <!-- While the tables reload, a loader stands in for the count: a partial sum reads as wrong. -->
      <button type="button" class="drawer-apply" [class.is-zero]="!resultsLoading && totalResults === 0"
              [attr.aria-busy]="resultsLoading || null" (click)="closePane()">
        <ng-container *ngIf="!resultsLoading; else resultsPending">VIEW RESULTS ({{totalResults}})</ng-container>
        <ng-template #resultsPending>VIEW RESULTS<rms-loading-spinner [inline]="true"></rms-loading-spinner></ng-template>
      </button>
    </div>
  </div>
</div>

